import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Phone,
  Copy,
  Check,
  Calendar,
  Clock,
  MapPin,
  Navigation,
  User,
  UserCheck,
  FileText,
  AlertTriangle,
  ChevronDown,
  Compass,
  CheckCircle2,
  Maximize2,
  RefreshCw,
  Loader2,
  Activity,
  CreditCard,
  Building2,
  Car,
  ShieldCheck,
  Map as MapIcon,
  HelpCircle,
  Link2,
  Pencil,
  Ban,
} from 'lucide-react';
import commandCenterService from '../../services/commandCenterService';
import CommandCenterTripLiveMap from './CommandCenterTripLiveMap';
import { deriveTripStage } from './mockTrackingData';
import PaymentLinkModal from '../payment/PaymentLinkModal';
import PatientLocationSharePopup from './PatientLocationSharePopup';
import { usePatientLocationShare, describeLocationShare, bookingPatientPhone } from '../../hooks/usePatientLocationShare';
import { getAmbulanceTypeAbbreviation } from '../../utils/ambulanceTypeHelper';

const STAGE_CONFIG = {
  'Requested': { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500', border: 'border-violet-200' },
  'Pending': { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400', border: 'border-slate-200' },
  'Scheduled': { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500', border: 'border-blue-100' },
  'Active Trips': { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', border: 'border-blue-200' },
  'Towards Pickup': { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', border: 'border-blue-200' },
  'At Pickup': { bg: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500', border: 'border-cyan-200' },
  'Towards Drop': { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500', border: 'border-purple-200' },
  'At Drop': { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', dot: 'bg-fuchsia-500', border: 'border-fuchsia-200' },
  'Untracked': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', border: 'border-amber-200' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-200' },
  'Cancelled': { bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500', border: 'border-rose-200' },
  'Rejected': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', border: 'border-red-200' },
};

// Format helper for dates & times
const formatDateTime = (dateStr) => {
  if (!dateStr) return { date: '—', time: '—', full: '—', timeWithSec: '—' };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { date: '—', time: '—', full: '—', timeWithSec: '—' };

  const day = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  const timeWithSec = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });

  return {
    date: day,
    time: time,
    timeWithSec: timeWithSec,
    full: `${day} · ${time}`,
  };
};

const formatDurationText = (seconds) => {
  if (seconds == null || isNaN(seconds) || seconds < 0) return '—';
  if (seconds === 0) return '0 min 00 sec';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins === 0) return `${secs} sec`;
  return `${mins} min ${String(secs).padStart(2, '0')} sec`;
};

const CopyablePhone = ({
  phone,
  callLabel = 'Call',
  showIcon = false,
  iconSize = 'w-3 h-3',
  textColor = 'text-blue-600 font-semibold',
  className = '',
  callClassName = 'hover:underline',
}) => {
  const [copied, setCopied] = useState(false);

  if (!phone || phone === '—' || phone === 'Not provided') {
    return <span className="text-gray-400 italic text-xs">Not provided</span>;
  }

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(String(phone).trim());
      }
    } catch (err) {
      console.warn('Clipboard write failed:', err);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className={`inline-flex items-center gap-1 group/copyable ${className}`}>
      <a
        href={`tel:${phone}`}
        onClick={(e) => e.stopPropagation()}
        className={`inline-flex items-center gap-1 ${textColor} ${callClassName} transition-colors`}
        title={`${callLabel}: ${phone}`}
      >
        {showIcon && <Phone className={`${iconSize} shrink-0`} />}
        <span>{phone}</span>
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className={`inline-flex items-center justify-center p-0.5 rounded transition-all cursor-pointer ${
          copied
            ? 'text-emerald-600 bg-emerald-50 scale-105'
            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50/80 active:scale-95'
        }`}
        title={copied ? 'Copied to clipboard!' : `Copy ${phone}`}
        aria-label="Copy phone number"
      >
        {copied ? (
          <Check className={`${iconSize} stroke-[2.5] text-emerald-600`} />
        ) : (
          <Copy className={`${iconSize} opacity-70 group-hover/copyable:opacity-100 transition-opacity`} />
        )}
      </button>
    </div>
  );
};

// Render 1 phone number directly without dummy placeholders, or 2-3 numbers if available
const renderContactList = (contactNumbers, emptyMessage = 'Not provided') => {
  const valid = (contactNumbers || [])
    .map((num) => (num ? String(num).trim() : ''))
    .filter((num) => num && num !== 'Not provided' && num !== '—' && num !== 'null' && num !== 'undefined');

  // Deduplicate
  const uniqueNumbers = Array.from(new Set(valid));

  if (uniqueNumbers.length === 0) {
    return <div className="text-xs text-gray-400 italic py-0.5">{emptyMessage}</div>;
  }

  // If only 1 number is available: show ONLY that single number row
  if (uniqueNumbers.length === 1) {
    const single = uniqueNumbers[0];
    return (
      <div className="flex items-center justify-between text-xs py-0.5">
        <span className="text-gray-400 font-medium">Number 1</span>
        <CopyablePhone phone={single} />
      </div>
    );
  }

  // If 2 or 3 numbers exist: show Number 1, Number 2, Number 3
  return (
    <div className="space-y-1 text-xs">
      {uniqueNumbers.slice(0, 3).map((num, idx) => (
        <div key={idx} className="flex items-center justify-between text-xs py-0.5">
          <span className="text-gray-400 font-medium">Number {idx + 1}</span>
          <CopyablePhone phone={num} />
        </div>
      ))}
    </div>
  );
};

const CommandCenterTripDetailsModal = ({
  trip,
  onClose,
  onAssignDriver,
  onViewMap,
  onCancelBooking,
  onPaymentLinkPaid,
  onBookingUpdated,
}) => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [copiedKey, setCopiedKey] = useState(null);
  const [fullBooking, setFullBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [gpsSimulatedLoss, setGpsSimulatedLoss] = useState(false);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);
  const [showPaymentLink, setShowPaymentLink] = useState(false);
  const [paidViaLink, setPaidViaLink] = useState(false);
  // Where the patient says they are (via the SMS / WhatsApp link) - an extra map marker, pickup is untouched
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const { share: locationShare, patientLocation, refresh: refreshLocationShare } = usePatientLocationShare(trip?.id, Boolean(trip));
  // const [lastUpdatedSec, setLastUpdatedSec] = useState(5);

  // Patient details editing state
  const [isEditingPatient, setIsEditingPatient] = useState(false);
  const [patientEditForm, setPatientEditForm] = useState({
    name: '',
    phone: '',
    contactPhone: '',
    age: '',
    gender: '',
    condition: '',
  });
  const [isUpdatingPatient, setIsUpdatingPatient] = useState(false);
  const [patientUpdateSuccess, setPatientUpdateSuccess] = useState(false);
  const [patientUpdateError, setPatientUpdateError] = useState('');

  const scrollContainerRef = useRef(null);
  const paymentDropdownRef = useRef(null);

  // Close payment dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(e.target)) {
        setPaymentDropdownOpen(false);
      }
    };
    if (paymentDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [paymentDropdownOpen]);

  // Fetch full detailed booking data from backend on mount or trip id change
  useEffect(() => {
    let isMounted = true;
    if (!trip?.id) return;

    setIsLoading(true);
    commandCenterService
      .getBooking(trip.id)
      .then((res) => {
        if (!isMounted) return;
        const data = res?.data || res;
        if (data) {
          setFullBooking(data);
          if (data.payment_status) {
            setSelectedPaymentStatus(data.payment_status);
          }
        }
      })
      .catch((err) => {
        console.warn('Could not fetch full booking details from backend, falling back to trip row:', err);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [trip?.id]);

  // "Last updated: X sec ago" dynamic ticker (commented out: counts 5->60s with no real GPS data behind it)
  /*
  useEffect(() => {
    const timer = setInterval(() => {
      setLastUpdatedSec((prev) => (prev >= 60 ? 5 : prev + 5));
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  */

  // Reset scroll on trip change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [trip?.id]);

  if (!trip) return null;

  const raw = fullBooking || trip.rawBooking || {};
  const customer = trip.customer || {};
  const driver = fullBooking?.driver || trip.driver || null;
  const hospital = fullBooking?.hospital || trip.hospital || null;
  const vehicle = fullBooking?.vehicle || raw.vehicle || (trip.driver ? {
    plate_number: trip.driver.vehiclePlate !== '—' ? trip.driver.vehiclePlate : null,
    type: { name: trip.driver.vehicleType !== '—' ? trip.driver.vehicleType : null },
    operator: {
      name: trip.driver.operatorName !== '—' ? trip.driver.operatorName : null,
      contact_info: trip.driver.operatorContact || null,
    },
  } : {});
  const fare = trip.fare || {};
  const statusHistory = fullBooking?.BookingStatusHistory || raw.BookingStatusHistory || [];

  const effectiveStage = trip.stage || (fullBooking ? deriveTripStage(fullBooking) : (raw ? deriveTripStage(raw) : 'Pending'));
  const isEmergency = trip.priority === 'EMERGENCY' || raw.priority_level === 'EMERGENCY';
  const isCancelled =
    effectiveStage === 'Cancelled' ||
    raw.status === 'CANCELLED' ||
    trip.stage === 'Cancelled';

  // Find cancellation event from status history
  const cancellationHistory = [...statusHistory]
    .reverse()
    .find((h) => String(h.new_status).toUpperCase() === 'CANCELLED' && (h.reason || h.notes));

  const cancellationReason =
    cancellationHistory?.reason ||
    trip.cancelReason ||
    raw.cancellation_reason ||
    (raw.notes && raw.notes.startsWith('Cancelled:') ? raw.notes.replace(/^Cancelled:\s*/, '') : null);

  const cancelledAt =
    cancellationHistory?.created_at ||
    trip.cancelledAt ||
    (isCancelled ? (raw.updated_at || trip.updatedAt) : null);

  // Booking ID & created at
  const bookingId = trip.id || raw.booking_id || '—';
  const createdDateComp = formatDateTime(raw.created_at || trip.createdAt || trip.rawBooking?.created_at || new Date());
  const scheduledDateComp = raw.scheduled_datetime ? formatDateTime(raw.scheduled_datetime) : null;

  // Locations & Addresses
  const pickupAddress = raw.pickup_address || trip.pickup?.address || '';
  const dropoffAddress = raw.dropoff_address || trip.dropoff?.address || '';

  const pickupLocationName =
    trip.pickup?.locationName ||
    (pickupAddress ? pickupAddress.split(',')[0].trim() : 'Pickup location');

  const dropoffLocationName =
    hospital?.name ||
    raw.hospital_name_text ||
    trip.dropoff?.locationName ||
    (dropoffAddress ? dropoffAddress.split(',')[0].trim() : 'Drop location');

  // Coordinates for Map
  const parseCoord = (val, defaultVal) => {
    const num = parseFloat(val);
    return isFinite(num) ? num : defaultVal;
  };

  const pLat = parseCoord(raw.pickup_latitude || trip.pickup?.latitude || trip.pickup?.lat, 23.0305);
  const pLng = parseCoord(raw.pickup_longitude || trip.pickup?.longitude || trip.pickup?.lng, 72.5855);
  const dLat = parseCoord(raw.dropoff_latitude || trip.dropoff?.latitude || trip.dropoff?.lat, 23.1098);
  const dLng = parseCoord(raw.dropoff_longitude || trip.dropoff?.longitude || trip.dropoff?.lng, 72.5451);
  const aLat = parseCoord(vehicle.current_latitude || vehicle.latitude || driver?.latitude, null) ?? (pLat && dLat ? (pLat + dLat) / 2 : 23.0701);
  const aLng = parseCoord(vehicle.current_longitude || vehicle.longitude || driver?.longitude, null) ?? (pLng && dLng ? (pLng + dLng) / 2 : 72.5653);

  const pickupCoords = { latitude: pLat, longitude: pLng, lat: pLat, lng: pLng };
  const dropoffCoords = { latitude: dLat, longitude: dLng, lat: dLat, lng: dLng };
  const ambulanceCoords = { latitude: aLat, longitude: aLng, lat: aLat, lng: aLng };

  // Contacts
  const patientName = customer.name || raw.patient_name || raw.customer_name || raw.contact_person_name || 'Patient';
  const patientPhone = customer.phone || raw.patient_phone || raw.contact_person_phone || null;
  const patientAltPhone =
    raw.contact_person_phone && raw.contact_person_phone !== patientPhone
      ? raw.contact_person_phone
      : null;

  const driverName = driver?.name || 'Not assigned';
  const driverPhone = driver?.phone || null;
  const vehiclePlate = vehicle.plate_number || driver?.vehiclePlate || 'Not assigned';
  const vehicleFullName =
    vehicle.type?.name || driver?.vehicleType || trip.service || raw.service_type || (raw.requested_vehicle_types?.[0]) || 'General Ambulance';
  const vehicleAbbreviation =
    vehicle.type?.abbreviation || getAmbulanceTypeAbbreviation(vehicleFullName) || vehicleFullName;
  const vehicleType = vehicleAbbreviation;

  const ownerName = vehicle.operator?.name || trip.driver?.operatorName || 'Not provided';
  const ownerContact = vehicle.operator?.contact_info || trip.driver?.operatorContact || null;

  // Real contact arrays for dynamic rendering
  const patientNumbers = [patientPhone, patientAltPhone, hospital?.contact_person_phone].filter(Boolean);
  const driverNumbers = [driverPhone, driver?.emergency_contact].filter(Boolean);
  const ownerNumbers = [ownerContact, vehicle.operator?.emergency_contact].filter(Boolean);

  const startEditingPatient = () => {
    setPatientEditForm({
      name: fullBooking?.patient_name || customer.name || raw.patient_name || raw.customer_name || 'Patient',
      phone: fullBooking?.patient_phone || customer.phone || raw.patient_phone || '',
      contactPhone: fullBooking?.contact_person_phone || raw.contact_person_phone || '',
      age: fullBooking?.patient_age || raw.patient_age ? String(fullBooking?.patient_age || raw.patient_age) : '',
      gender: (fullBooking?.patient_gender || raw.patient_gender || '').toUpperCase(),
      condition: fullBooking?.patient_condition || raw.patient_condition || '',
    });
    setPatientUpdateError('');
    setIsEditingPatient(true);
  };

  const handleSavePatientUpdates = async () => {
    if (!patientEditForm.name.trim()) {
      setPatientUpdateError('Patient name cannot be empty');
      return;
    }
    setIsUpdatingPatient(true);
    setPatientUpdateError('');
    try {
      const payload = {
        patientName: patientEditForm.name.trim(),
        patientPhone: patientEditForm.phone.trim() || undefined,
        contactPersonPhone: patientEditForm.contactPhone.trim() || undefined,
        patientAge: patientEditForm.age ? Number(patientEditForm.age) : undefined,
        patientGender: patientEditForm.gender ? patientEditForm.gender.toUpperCase() : undefined,
        patientCondition: patientEditForm.condition?.trim() || undefined,
      };
      const res = await commandCenterService.updateBooking(trip.id, payload);
      const updatedData = res?.data || res;
      setFullBooking((prev) => ({
        ...(prev || {}),
        ...payload,
        patient_name: payload.patientName,
        patient_phone: payload.patientPhone,
        contact_person_phone: payload.contactPersonPhone,
        patient_age: payload.patientAge,
        patient_gender: payload.patientGender,
        patient_condition: payload.patientCondition,
      }));
      setIsEditingPatient(false);
      setPatientUpdateSuccess(true);
      setTimeout(() => setPatientUpdateSuccess(false), 3500);
      onBookingUpdated?.(updatedData);
    } catch (err) {
      setPatientUpdateError(err.message || 'Failed to update patient details');
    } finally {
      setIsUpdatingPatient(false);
    }
  };

  // Distance & ETA (strictly from backend when present)
  const rawDist = trip.distance?.estimatedKm ?? raw.actual_distance_km ?? raw.estimated_distance_km;
  const remainingKm = rawDist != null && !isNaN(Number(rawDist)) ? parseFloat(rawDist).toFixed(1) : null;

  const rawMins = raw.actual_duration_minutes ?? raw.estimated_duration_minutes;
  const remainingMinutes = rawMins != null && !isNaN(Number(rawMins))
    ? Math.round(Number(rawMins))
    : remainingKm
    ? Math.max(2, Math.round((parseFloat(remainingKm) / 30) * 60))
    : null;

  const etaTimestamp = remainingMinutes != null
    ? new Date(Date.now() + remainingMinutes * 60000).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  // Financials (strictly from backend)
  const patientChargeNum = Number(
    raw.final_fare ?? raw.total_price ?? raw.platform_service_fee ?? raw.agreed_price ?? raw.fleet_operator_price ?? raw.offered_price ?? raw.estimated_fare ?? raw.system_price ?? fare.finalFare ?? fare.estimatedFare ?? 0
  );
  const patientChargeFormatted = patientChargeNum > 0 ? patientChargeNum.toLocaleString('en-IN') : '—';
  const vendorPayableNum = Number(
    raw.fleet_operator_service_fee ?? raw.partner_price ?? (raw.partner_share_percent && patientChargeNum > 0 ? Math.round((patientChargeNum * Number(raw.partner_share_percent)) / 100) : 0)
  );
  const vendorPayableFormatted = vendorPayableNum > 0 ? vendorPayableNum.toLocaleString('en-IN') : '—';

  const currentPaymentStatus = useMemo(() => {
    if (paidViaLink) return 'Paid';
    const rawStatus = String(selectedPaymentStatus || raw.payment_status || 'Pending').trim().toLowerCase();
    if (rawStatus === 'paid') return 'Paid';
    if (rawStatus === 'failed') return 'Failed';
    return 'Pending';
  }, [paidViaLink, selectedPaymentStatus, raw.payment_status]);

  const handleCopy = (text, key) => {
    if (!text || text === '—') return;
    navigator.clipboard?.writeText(String(text));
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Timeline Stepper definitions & stage transition calculations from real timestamps
  const timelineStages = useMemo(() => {
    const stageRank = {
      'Pending': 0,
      'Scheduled': 1,
      'Towards Pickup': 2,
      'At Pickup': 3,
      'Towards Drop': 4,
      'At Drop': 5,
      'Completed': 6,
    };

    const currentRank = stageRank[effectiveStage] ?? (driver ? 2 : 1);

    const findStatusTime = (targetStatuses) => {
      const match = (statusHistory || []).find((h) =>
        targetStatuses.includes(String(h.new_status).toUpperCase())
      );
      return match?.created_at || null;
    };

    const loggedAt = raw.created_at || trip.createdAt || null;
    const acceptedAt =
      findStatusTime(['ASSIGNED', 'CONFIRMED']) ||
      (driver ? (raw.assigned_at || raw.updated_at) : null);
    const towardsPickupAt =
      findStatusTime(['IN_PROGRESS', 'TOWARDS_PICKUP']) ||
      (currentRank >= 2 ? (acceptedAt || loggedAt) : null);
    const atPickupAt =
      raw.arrived_at ||
      findStatusTime(['AT_PICKUP', 'ARRIVED']) ||
      null;
    const towardsDropAt =
      raw.Trip?.start_time ||
      findStatusTime(['TOWARDS_DROPOFF', 'IN_TRANSIT_DROPOFF']) ||
      null;
    const atDropAt =
      raw.at_dropoff_at ||
      findStatusTime(['AT_DROPOFF']) ||
      null;
    const completedAt =
      raw.status === 'COMPLETED'
        ? (raw.Trip?.end_time || findStatusTime(['COMPLETED']) || raw.updated_at)
        : null;

    const calcDuration = (startStr, endStr) => {
      if (!startStr || !endStr) return null;
      const t1 = new Date(startStr).getTime();
      const t2 = new Date(endStr).getTime();
      if (isNaN(t1) || isNaN(t2) || t2 < t1) return null;
      return Math.round((t2 - t1) / 1000);
    };

    const fmtTime = (isoStr, fallback = 'Pending') => {
      if (!isoStr) return fallback;
      const dt = formatDateTime(isoStr);
      return dt.timeWithSec !== '—' ? dt.timeWithSec : dt.time !== '—' ? dt.time : fallback;
    };

    // Stage 1: Logged
    const stage1Time = fmtTime(loggedAt, createdDateComp.timeWithSec !== '—' ? createdDateComp.timeWithSec : 'Logged');

    // Stage 2: Accepted
    const stage2Time = currentRank >= 1 && acceptedAt ? fmtTime(acceptedAt) : driver ? 'Assigned' : 'Pending';
    const dur1_2 = calcDuration(loggedAt, acceptedAt);
    const stage2Badge = dur1_2 != null ? formatDurationText(dur1_2) : driver ? 'Assigned' : '—';

    // Stage 3: Towards Pickup
    const stage3Time = currentRank >= 2 && towardsPickupAt ? fmtTime(towardsPickupAt) : currentRank >= 2 ? 'En route' : 'Pending';
    const dur2_3 = calcDuration(acceptedAt || loggedAt, towardsPickupAt);
    const stage3Badge = dur2_3 != null ? formatDurationText(dur2_3) : currentRank >= 2 ? (currentRank === 2 ? 'In progress' : 'Done') : '—';

    // Stage 4: At Pickup
    const stage4Time = currentRank >= 3 && atPickupAt ? fmtTime(atPickupAt) : currentRank >= 3 ? 'Arrived' : 'Pending';
    const dur3_4 = calcDuration(towardsPickupAt, atPickupAt);
    const stage4Badge = dur3_4 != null ? formatDurationText(dur3_4) : currentRank >= 3 ? (currentRank === 3 ? 'In progress' : 'Arrived') : '—';

    // Stage 5: Towards Drop
    const stage5Time = currentRank >= 4 && towardsDropAt ? fmtTime(towardsDropAt) : currentRank >= 4 ? 'Departed' : 'Pending';
    const dur4_5 = calcDuration(atPickupAt, towardsDropAt);
    const stage5Badge = dur4_5 != null ? formatDurationText(dur4_5) : currentRank >= 4 ? (currentRank === 4 ? 'In progress' : 'Departed') : '—';

    // Stage 6: At Drop
    const stage6Time = currentRank >= 5 && atDropAt ? fmtTime(atDropAt) : currentRank >= 5 ? 'Reached drop' : 'Pending';
    const dur5_6 = calcDuration(towardsDropAt, atDropAt);
    const stage6Badge = dur5_6 != null ? formatDurationText(dur5_6) : currentRank === 5 ? 'In progress' : currentRank > 5 ? 'Reached' : '—';

    // Stage 7: Completed
    const stage7Time = currentRank >= 6 && completedAt ? fmtTime(completedAt) : currentRank >= 6 ? 'Completed' : 'Pending';
    const dur6_7 = calcDuration(atDropAt, completedAt);
    const stage7Badge = dur6_7 != null ? formatDurationText(dur6_7) : currentRank >= 6 ? 'Done' : '—';

    return [
      {
        id: 'logged',
        name: 'Request Accepted / Logged',
        time: stage1Time,
        badgeLabel: 'Trip request',
        badgeValue: 'Start',
        badgeStyle: 'bg-emerald-50 text-emerald-700',
        status: 'completed',
      },
      {
        id: 'accepted',
        name: 'Accepted by RISA Saarthi',
        time: stage2Time,
        badgeLabel: 'Since logged',
        badgeValue: stage2Badge,
        badgeStyle: 'bg-cyan-50 text-cyan-700',
        status: currentRank > 1 ? 'completed' : currentRank === 1 ? 'active' : 'pending',
      },
      {
        id: 'towards_pickup',
        name: 'Towards Pickup',
        time: stage3Time,
        badgeLabel: 'Since accepted',
        badgeValue: stage3Badge,
        badgeStyle: 'bg-blue-50 text-blue-700',
        status: currentRank > 2 ? 'completed' : currentRank === 2 ? 'active' : 'pending',
      },
      {
        id: 'at_pickup',
        name: 'At Pick Up',
        time: stage4Time,
        badgeLabel: 'Pickup travel',
        badgeValue: stage4Badge,
        badgeStyle: 'bg-emerald-50 text-emerald-700',
        status: currentRank > 3 ? 'completed' : currentRank === 3 ? 'active' : 'pending',
      },
      {
        id: 'towards_drop',
        name: 'Towards Drop',
        time: stage5Time,
        badgeLabel: 'Time at pickup',
        badgeValue: stage5Badge,
        badgeStyle: 'bg-purple-50 text-purple-700 font-bold',
        status: currentRank > 4 ? 'completed' : currentRank === 4 ? 'active' : 'pending',
      },
      {
        id: 'at_drop',
        name: 'At Drop',
        time: stage6Time,
        badgeLabel: 'Drop travel',
        badgeValue: stage6Badge,
        badgeStyle: 'bg-emerald-50 text-emerald-700',
        status: currentRank > 5 ? 'completed' : currentRank === 5 ? 'active' : 'pending',
      },
      {
        id: 'completed',
        name: 'Completed',
        time: stage7Time,
        badgeLabel: 'Time at drop',
        badgeValue: stage7Badge,
        badgeStyle: currentRank >= 6 ? 'bg-emerald-50 text-emerald-700' : 'text-gray-400',
        status: currentRank >= 6 ? 'completed' : 'pending',
      },
    ];
  }, [effectiveStage, createdDateComp, driver, raw, statusHistory, trip]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/60 backdrop-blur-xs overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-gray-200/90 w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden my-auto transform transition-all text-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Breadcrumb & Header Bar */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4 bg-white shrink-0">
          <div className="space-y-1 min-w-0">
            <div className="text-[11px] font-bold text-gray-400 tracking-wider uppercase">
              RISA / TRIP OVERVIEW
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                <span>Booking #{bookingId}</span>
                <button
                  onClick={() => handleCopy(bookingId, 'booking_id')}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer p-0.5"
                  title="Copy Booking ID"
                >
                  {copiedKey === 'booking_id' ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </h2>

              {/* Priority Badge */}
              {isEmergency ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                  <AlertTriangle className="w-3 h-3" />
                  Emergency
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200/60">
                  Non-emergency
                </span>
              )}

              {/* Stage Badge */}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200/70">
                {effectiveStage}
              </span>
            </div>

            <div className="text-xs text-gray-400 font-normal">
              Created {createdDateComp.full}
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0 pt-1">
            {/*
            <div className="text-xs text-emerald-600 font-medium hidden sm:inline-block">
              Last Updated: {lastUpdatedSec} sec ago
            </div>
            */}
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer shadow-2xs"
            >
              <X className="w-3.5 h-3.5" />
              <span>Close</span>
            </button>
          </div>
        </div>

        {/* Scrollable Modal Content */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-white"
        >
          {/* Section 1: Pickup, Scheduled Time & Drop Location (3 Columns) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-1 pb-4 border-b border-gray-100">
            {/* Col 1: Pickup */}
            <div>
              <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
                PICKUP LOCATION &amp; ADDRESS
              </div>
              <div className="text-sm font-bold text-gray-900 mt-1">
                {pickupLocationName}
              </div>
              <div className="text-xs text-gray-400 mt-0.5 break-words">
                Full pickup address: {pickupAddress || 'Not provided'}
              </div>
              {!isCancelled && effectiveStage !== 'Completed' && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setShowLocationPopup(true)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors cursor-pointer"
                    title="Send the patient a link to share their location"
                  >
                    <MapPin className="w-3 h-3" />
                    <span>{locationShare ? 'Patient location' : 'Request patient location'}</span>
                  </button>
                  {locationShare && (
                    <span className="text-[11px] font-medium text-gray-500">{describeLocationShare(locationShare)}</span>
                  )}
                  {patientLocation && (
                    <a
                      href={`https://www.google.com/maps?q=${patientLocation.latitude},${patientLocation.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] font-medium text-blue-600 hover:underline"
                    >
                      Open patient location in Maps
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Col 2: Date & Time */}
            <div>
              <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
                PICKUP DATE &amp; TIME
              </div>
              <div className="text-sm font-bold text-gray-900 mt-1">
                {scheduledDateComp?.full || createdDateComp.full}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {scheduledDateComp ? 'Scheduled pickup' : 'Immediate pickup'} · IST
              </div>
            </div>

            {/* Col 3: Drop */}
            <div>
              <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
                DROP LOCATION &amp; ADDRESS
              </div>
              <div className="text-sm font-bold text-gray-900 mt-1">
                {dropoffLocationName}
              </div>
              <div className="text-xs text-gray-400 mt-0.5 break-words">
                Full drop address: {dropoffAddress || 'Not provided'}
              </div>
            </div>
          </div>

          {/* Section 2: Contact Details (3 Columns) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-1 pb-4 border-b border-gray-100">
            {/* Col 1: Patient Contacts */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
                  PATIENT CONTACT DETAILS
                </div>
                {!isEditingPatient && (
                  <button
                    type="button"
                    onClick={startEditingPatient}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors cursor-pointer"
                    title="Edit Patient Details"
                  >
                    <Pencil className="w-2.5 h-2.5" />
                    <span>Edit</span>
                  </button>
                )}
              </div>

              {patientUpdateSuccess && (
                <div className="mb-2 p-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center gap-1.5 font-medium animate-in fade-in">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Patient details updated successfully</span>
                </div>
              )}

              {isEditingPatient ? (
                <div className="space-y-2 p-3 bg-gray-50/80 rounded-xl border border-gray-200 text-xs animate-in fade-in">
                  {patientUpdateError && (
                    <div className="text-red-600 text-[11px] font-medium bg-red-50 p-1.5 rounded border border-red-200">
                      {patientUpdateError}
                    </div>
                  )}
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">
                      Patient Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={patientEditForm.name}
                      onChange={(e) => setPatientEditForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter patient name"
                      className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">
                      Patient Phone
                    </label>
                    <input
                      type="tel"
                      value={patientEditForm.phone}
                      onChange={(e) => setPatientEditForm((prev) => ({ ...prev, phone: e.target.value }))}
                      placeholder="Enter phone number"
                      className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">
                      Alt Contact / Attendant Phone
                    </label>
                    <input
                      type="tel"
                      value={patientEditForm.contactPhone}
                      onChange={(e) => setPatientEditForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
                      placeholder="Enter alternate contact"
                      className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">
                        Age
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="120"
                        value={patientEditForm.age}
                        onChange={(e) => setPatientEditForm((prev) => ({ ...prev, age: e.target.value }))}
                        placeholder="Age"
                        className="w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">
                        Gender
                      </label>
                      <select
                        value={patientEditForm.gender}
                        onChange={(e) => setPatientEditForm((prev) => ({ ...prev, gender: e.target.value }))}
                        className="w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Select</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-0.5">
                      Patient Condition
                    </label>
                    <input
                      type="text"
                      value={patientEditForm.condition}
                      onChange={(e) => setPatientEditForm((prev) => ({ ...prev, condition: e.target.value }))}
                      placeholder="e.g. Stable, Critical, Cardiac, etc."
                      className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                    />
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingPatient(false);
                        setPatientUpdateError('');
                      }}
                      disabled={isUpdatingPatient}
                      className="px-2.5 py-1 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 border border-gray-200 cursor-pointer disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSavePatientUpdates}
                      disabled={isUpdatingPatient}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-xs cursor-pointer disabled:opacity-50 transition-colors"
                    >
                      {isUpdatingPatient ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      <span>{isUpdatingPatient ? 'Updating…' : 'Update'}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-sm font-bold text-gray-900 mt-1 mb-2">
                    {patientName}
                  </div>
                  {renderContactList(patientNumbers, 'No patient phone provided')}
                </>
              )}
            </div>

            {/* Col 2: Driver / Saarthi Contacts */}
            <div>
              <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
                RISA SAARTHI CONTACT DETAILS
              </div>
              <div className="text-sm font-bold text-gray-900 mt-1 mb-2">
                {driver ? driverName : 'Not assigned'}
              </div>
              {renderContactList(driverNumbers, driver ? 'No driver contact provided' : 'Driver not assigned')}
              <div className="text-xs text-gray-500 mt-2.5 pt-1 border-t border-gray-50">
                Ambulance: <span className="font-bold text-gray-800">{vehiclePlate}</span> · Type:{' '}
                <span
                  className="font-semibold text-gray-800 cursor-help"
                  title={vehicleFullName}
                >
                  {vehicleAbbreviation}
                </span>
                {/* Full name commented out for now - hover shows full name: */}
                {/* <span className="text-gray-500 text-xs ml-1">({vehicleFullName})</span> */}
              </div>
            </div>

            {/* Col 3: Ambulance Owner / Fleet Contacts */}
            <div>
              <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
                AMBULANCE OWNER CONTACT DETAILS
              </div>
              <div className="text-sm font-bold text-gray-900 mt-1 mb-2">
                Owner name: {ownerName}
              </div>
              {renderContactList(ownerNumbers, 'No owner contact provided')}
            </div>
          </div>

          {/* Section 3: Trip Timeline Stepper Card */}
          <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-2xs">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-bold text-gray-900">Trip timeline</span>
              </div>
              <div className="text-xs text-gray-400">
                {createdDateComp.date} · IST
              </div>
            </div>

            {/* Horizontal Flow Stepper + Fixed Payment Column */}
            <div className="pt-4 flex items-center justify-between gap-2 relative">
              {/* Stepper Nodes Scroll Container */}
              <div className="flex-1 overflow-x-auto pb-2 min-w-0">
                <div className="flex items-start justify-between min-w-[700px] gap-2 pr-2">
                  {timelineStages.map((stg, idx) => {
                    const isCompleted = stg.status === 'completed';
                    const isActive = stg.status === 'active';

                    return (
                      <div key={stg.id} className="flex-1 flex flex-col items-center text-center relative px-1">
                        {/* Connector Line to next stage */}
                        {idx < timelineStages.length - 1 && (
                          <div
                            className={`absolute top-2.5 left-1/2 w-full h-[2px] -z-0 ${
                              isCompleted ? 'bg-emerald-300' : 'bg-gray-200'
                            }`}
                          />
                        )}

                        {/* Node Icon */}
                        <div className="relative z-10 mb-2">
                          {isCompleted ? (
                            <div className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-600 flex items-center justify-center ring-4 ring-white shadow-2xs">
                              <Check className="w-3 h-3 text-emerald-600 stroke-[2.5]" />
                            </div>
                          ) : isActive ? (
                            <div className="w-5 h-5 rounded-full border-2 border-purple-600 bg-white flex items-center justify-center ring-4 ring-purple-100 shadow-2xs">
                              <div className="w-2 h-2 rounded-full bg-purple-600" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full border border-gray-300 bg-white flex items-center justify-center ring-4 ring-white">
                              <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                            </div>
                          )}
                        </div>

                        {/* Stage Name */}
                        <div className={`text-[11px] leading-tight mb-1 font-semibold ${
                          isActive ? 'text-purple-700 font-bold' : isCompleted ? 'text-gray-800' : 'text-gray-400'
                        }`}>
                          {stg.name}
                        </div>

                        {/* Stage Timestamp */}
                        <div className="text-[10px] text-gray-400 mb-2">
                          {stg.time}
                        </div>

                        {/* Stage Sub-Card / Duration Badge */}
                        <div className="w-full max-w-[105px] border border-gray-100 rounded-lg p-1.5 bg-gray-50/60 shadow-2xs">
                          <div className="text-[9px] text-gray-400 font-medium truncate">
                            {stg.badgeLabel}
                          </div>
                          <div className={`text-[11px] font-bold mt-0.5 truncate rounded px-1 py-0.5 ${stg.badgeStyle}`}>
                            {stg.badgeValue}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Far Right: Payment Section - Outside overflow container, no clipping */}
              <div
                ref={paymentDropdownRef}
                className="shrink-0 flex flex-col items-center justify-center pl-4 border-l border-gray-200 relative self-stretch"
              >
                <div className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 mb-1">
                  <span className="w-4 h-4 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold">
                    ₹
                  </span>
                  <span>Payment</span>
                </div>

                <div className="relative mt-1">
                  <button
                    type="button"
                    onClick={() => setPaymentDropdownOpen((prev) => !prev)}
                    className="inline-flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-50 text-amber-900 border border-amber-300 hover:bg-amber-100/80 transition-all cursor-pointer shadow-2xs min-w-[96px]"
                  >
                    <span>{currentPaymentStatus}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-amber-600 transition-transform duration-150 ${paymentDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {paymentDropdownOpen && (
                    <div className="absolute right-0 top-full mt-1.5 w-36 bg-white border border-gray-200 rounded-xl shadow-xl py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100">
                      {['Pending', 'Paid', 'Failed'].map((st) => {
                        const isSelected = currentPaymentStatus.toLowerCase() === st.toLowerCase();
                        return (
                          <button
                            key={st}
                            type="button"
                            onClick={() => {
                              setSelectedPaymentStatus(st);
                              setPaymentDropdownOpen(false);
                              if (trip?.id) {
                                commandCenterService.updateBooking(trip.id, { payment_status: st.toUpperCase() }).catch(() => {});
                              }
                            }}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                              isSelected ? 'bg-amber-50 text-amber-900 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  st === 'Paid'
                                    ? 'bg-emerald-500'
                                    : st === 'Failed'
                                    ? 'bg-rose-500'
                                    : 'bg-amber-500'
                                }`}
                              />
                              <span>{st}</span>
                            </div>
                            {isSelected && <Check className="w-3.5 h-3.5 text-amber-600" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="text-[10px] text-gray-400 mt-1.5">
                  Payment status
                </div>

                {/* Send / track a Razorpay payment link for this booking */}
                {currentPaymentStatus !== 'Paid' && (
                  <button
                    type="button"
                    onClick={() => setShowPaymentLink(true)}
                    className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer"
                  >
                    <Link2 className="w-3 h-3" />
                    <span>Payment link</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: 2 Columns (Left: Tabs & Details, Right: Live Tracking) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 pt-1">
            {/* Left 7 Cols: Tab Navigation & Tab Content Cards */}
            <div className="lg:col-span-7 space-y-4">
              {/* Tab Navigation Buttons */}
              <div className="flex items-center gap-1.5 border-b border-gray-200 pb-2.5">
                {['Overview', 'Commercials', 'Activity', 'Notes'].map((tab) => {
                  const isActive = activeTab === tab;
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                        isActive
                          ? 'bg-blue-100/70 text-blue-700 shadow-2xs'
                          : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      {tab}
                    </button>
                  );
                })}
              </div>

              {/* Tab 1: Overview */}
              {activeTab === 'Overview' && (
                <div className="space-y-4">
                  {/* Card 1: Booking & Acceptance */}
                  <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-2xs">
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-gray-700" />
                        <span className="text-sm font-bold text-gray-900">
                          Booking &amp; acceptance
                        </span>
                      </div>
                      <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded px-2 py-0.5">
                        {raw.is_app_tracked ? 'App Dispatch' : 'Manual Dispatch'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 text-xs">
                      {/* Left col */}
                      <div className="space-y-3">
                        <div>
                          <div className="text-gray-400 font-medium">Booked by / caller</div>
                          <div className="font-bold text-gray-900 mt-0.5">
                            {raw.booking_source === 'USER'
                              ? 'Patient / Self'
                              : raw.booking_source === 'HOSPITAL'
                              ? (hospital?.name || 'Hospital')
                              : (raw.booking_source || 'Direct')}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-400 font-medium">Assigned by</div>
                          <div className="font-bold text-gray-900 mt-0.5">
                            {raw.allocation_type === 'AUTOMATIC'
                              ? 'Auto Dispatch (System)'
                              : 'Manual Dispatch (Command Center)'}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-400 font-medium">Booking channel</div>
                          <div className="font-bold text-gray-900 mt-0.5">
                            {raw.source_channel ||
                              (raw.booking_source === 'USER' ? 'Patient Mobile App' : 'Command Center Dispatch')}
                          </div>
                        </div>
                      </div>

                      {/* Right col */}
                      <div className="space-y-3">
                        <div>
                          <div className="text-gray-400 font-medium">Request logged by</div>
                          <div className="font-bold text-gray-900 mt-0.5">
                            {raw.booking_source === 'USER'
                              ? (patientName || 'Patient')
                              : (hospital?.contact_person || raw.contact_person_name || 'Command Center Staff')}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-400 font-medium">Accepted by</div>
                          <div className="font-bold text-gray-900 mt-0.5">
                            {driver ? `${driverName} · RISA Saarthi` : 'Awaiting driver acceptance'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Finalized Commercials */}
                  <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-2xs">
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-700" />
                        <span className="text-sm font-bold text-gray-900">
                          Commercials
                        </span>
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                        {currentPaymentStatus}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3">
                      <div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          PATIENT CHARGE
                        </div>
                        <div className="text-2xl font-black text-gray-900 mt-0.5">
                          {patientChargeFormatted !== '—' ? `₹${patientChargeFormatted}` : '—'}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1">
                          Fare breakdown and settlement details in Commercials.
                        </div>
                      </div>

                      <div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          VENDOR PAYABLE
                        </div>
                        <div className="text-2xl font-black text-gray-900 mt-0.5">
                          {vendorPayableFormatted !== '—' ? `₹${vendorPayableFormatted}` : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2: Commercials */}
              {activeTab === 'Commercials' && (
                <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-2xs space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                    <span className="text-sm font-bold text-gray-900">Commercials &amp; Billing</span>
                    <span className="text-xs text-gray-400">Currency: INR (₹)</span>
                  </div>
                  <div className="space-y-2 text-xs">
                    {/* System Base Price commented out per request
                    <div className="flex items-center justify-between py-1 border-b border-gray-50">
                      <span className="text-gray-500">System Base Price</span>
                      <span className="font-semibold text-gray-800">
                        {raw.system_price ? `₹${Number(raw.system_price).toLocaleString('en-IN')}` : '—'}
                      </span>
                    </div>
                    */}
                    {raw.fleet_operator_price != null && (
                      <div className="flex items-center justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Fleet Operator Price</span>
                        <span className="font-semibold text-gray-800">
                          ₹{Number(raw.fleet_operator_price).toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                    {raw.platform_service_fee != null && (
                      <div className="flex items-center justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Platform Service Fee</span>
                        <span className="font-semibold text-gray-800">
                          ₹{Number(raw.platform_service_fee).toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                    {raw.fleet_operator_service_fee != null && (
                      <div className="flex items-center justify-between py-1 border-b border-gray-50">
                        <span className="text-gray-500">Operator Service Fee</span>
                        <span className="font-semibold text-gray-800">
                          ₹{Number(raw.fleet_operator_service_fee).toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between py-1 border-b border-gray-50">
                      <span className="text-gray-500">Distance</span>
                      <span className="font-semibold text-gray-800">
                        {remainingKm ? `${remainingKm} km` : '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1 border-b border-gray-50">
                      <span className="text-gray-500">Per Km Rate</span>
                      <span className="font-semibold text-gray-800">
                        {raw.per_km_rate ? `₹${raw.per_km_rate} / km` : '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1 border-b border-gray-50">
                      <span className="text-gray-500 font-bold">Total Patient Charge</span>
                      <span className="font-bold text-base text-gray-900">
                        {raw.total_price ? `₹${Number(raw.total_price).toLocaleString('en-IN')}` : (patientChargeFormatted !== '—' ? `₹${patientChargeFormatted}` : '—')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1 border-b border-gray-50">
                      <span className="text-gray-500">Vendor / Driver Payout</span>
                      <span className="font-bold text-sm text-emerald-700">
                        {vendorPayableFormatted !== '—' ? `₹${vendorPayableFormatted}` : '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span className="text-gray-500">Platform Margin</span>
                      <span className="font-semibold text-blue-700">
                        {patientChargeNum > 0 && vendorPayableNum > 0
                          ? `₹${(patientChargeNum - vendorPayableNum).toLocaleString('en-IN')}`
                          : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 3: Activity */}
              {activeTab === 'Activity' && (
                <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-2xs space-y-3">
                  <div className="text-sm font-bold text-gray-900 pb-2 border-b border-gray-100">
                    Status Transition History
                  </div>
                  {statusHistory && statusHistory.length > 0 ? (
                    <div className="space-y-2.5 text-xs">
                      {statusHistory.map((item, idx) => {
                        const isCancelItem = String(item.new_status).toUpperCase() === 'CANCELLED';
                        return (
                          <div key={idx} className="py-2 border-b border-gray-50 last:border-b-0">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${isCancelItem ? 'bg-rose-500' : 'bg-blue-500'}`} />
                                <span className={`font-medium ${isCancelItem ? 'text-rose-700 font-semibold' : 'text-gray-800'}`}>
                                  {item.old_status ? `${item.old_status} → ` : ''}{item.new_status}
                                </span>
                              </div>
                              <span className="text-gray-400">
                                {formatDateTime(item.created_at).timeWithSec}
                              </span>
                            </div>
                            {item.reason && (
                              <div className={`mt-1.5 ml-4 p-2 rounded-lg text-[11px] leading-relaxed border ${
                                isCancelItem
                                  ? 'bg-rose-50/80 text-rose-800 border-rose-200/60 font-medium'
                                  : 'bg-gray-50 text-gray-600 border-gray-100 italic'
                              }`}>
                                <span className="font-semibold">{isCancelItem ? 'Cancellation Reason: ' : 'Reason / Note: '}</span>
                                {item.reason}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 py-4 text-center">
                      No status transitions recorded yet.
                    </div>
                  )}
                </div>
              )}

              {/* Tab 4: Notes */}
              {activeTab === 'Notes' && (
                <div className="space-y-4">
                  {/* Cancellation Reason Card - Displayed when booking is Cancelled */}
                  {(isCancelled || cancellationReason) && (
                    <div className="border border-rose-200 rounded-2xl p-4 bg-gradient-to-br from-rose-50/70 to-red-50/30 shadow-2xs space-y-3">
                      <div className="flex items-center justify-between pb-2 border-b border-rose-200/60">
                        <div className="flex items-center gap-2">
                          <Ban className="w-4 h-4 text-rose-600 shrink-0" />
                          <span className="text-sm font-bold text-rose-950">
                            Cancellation Reason &amp; Details
                          </span>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200">
                          CANCELLED
                        </span>
                      </div>

                      <div className="space-y-2 text-xs">
                        <div>
                          <span className="text-rose-900/80 font-semibold">Cancellation Reason:</span>
                          <div className="mt-1 p-3 bg-white rounded-xl border border-rose-200/80 text-rose-950 font-medium text-xs leading-relaxed shadow-2xs">
                            {cancellationReason || 'No specific cancellation reason provided.'}
                          </div>
                        </div>

                        {cancelledAt && (
                          <div className="flex items-center justify-between pt-1 text-[11px] text-rose-800/80">
                            <span className="font-medium">Cancelled at:</span>
                            <span className="font-semibold text-rose-950">
                              {formatDateTime(cancelledAt).full}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Patient & Medical Notes Card */}
                  <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-2xs space-y-3">
                    <div className="text-sm font-bold text-gray-900 pb-2 border-b border-gray-100">
                      Patient &amp; Medical Notes
                    </div>
                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="text-gray-400 font-medium">Condition:</span>
                        <span className="ml-2 font-bold text-gray-800">{raw.patient_condition || 'Stable'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 font-medium">Age / Gender:</span>
                        <span className="ml-2 font-semibold text-gray-800">
                          {raw.patient_age ? `${raw.patient_age} yrs` : '—'} / {raw.patient_gender || '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 font-medium">Internal Notes:</span>
                        <div className="mt-1 p-2.5 bg-gray-50 rounded-lg text-gray-700 leading-relaxed">
                          {raw.notes || 'No internal notes provided for this booking.'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right 5 Cols: Live Tracking Card with Embedded Map */}
            <div className="lg:col-span-5">
              <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-2xs flex flex-col h-full">
                {/* Live tracking header */}
                <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <Navigation className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-bold text-gray-900">Live tracking</span>
                  </div>
                  {/* Always-green "GPS connected" badge commented out (simulated/fake)
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                    <span className={`w-2 h-2 rounded-full ${gpsSimulatedLoss ? 'bg-red-500' : 'bg-emerald-500 animate-pulse'}`} />
                    <span>{gpsSimulatedLoss ? 'GPS Loss (Simulated)' : 'GPS connected'}</span>
                  </div>
                  */}
                </div>

                {/* Target Arrival Banner */}
                <div className="py-3 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wide truncate">
                      ARRIVAL AT {dropoffLocationName}
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-50 text-purple-700">
                      {effectiveStage.toLowerCase()}
                    </span>
                  </div>

                  <div className="text-2xl font-extrabold text-purple-700 mt-1">
                    {remainingMinutes != null ? `${remainingMinutes} min` : 'Calculating...'}
                  </div>
                  <div className="text-xs text-gray-400 font-medium mt-0.5">
                    {etaTimestamp ? `ETA ${etaTimestamp}` : 'ETA pending'}
                    {remainingKm ? ` · ${remainingKm} km remaining` : ''}
                  </div>
                </div>

                {/* Embedded Live Map Container */}
                <div className="relative mt-3 rounded-xl overflow-hidden border border-gray-200 bg-gray-100 h-64 sm:h-72">
                  <CommandCenterTripLiveMap
                    pickupLocation={pickupCoords}
                    dropoffLocation={dropoffCoords}
                    ambulanceLocation={ambulanceCoords}
                    patientLocation={patientLocation}
                    stage={effectiveStage}
                    height="100%"
                    showLegend={false}
                  />

                  {/* Overlay Badges on Map */}
                  <div className="absolute top-2.5 left-2.5 pointer-events-none">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 text-xs font-semibold text-gray-800 shadow-xs border border-gray-200/60">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span>Live Location</span>
                    </div>
                  </div>

                  {onViewMap && (
                    <div className="absolute top-2.5 right-2.5">
                      <button
                        onClick={() => {
                          onViewMap({
                            ...trip,
                            rawBooking: raw,
                            pickup: pickupCoords,
                            dropoff: dropoffCoords,
                            ambulanceLocation: ambulanceCoords,
                            stage: effectiveStage,
                          });
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/95 text-xs font-semibold text-gray-700 shadow-xs border border-gray-200/80 hover:bg-white hover:text-blue-600 transition-colors cursor-pointer"
                        title="View Full Map"
                      >
                        <Maximize2 className="w-3.5 h-3.5" />
                        <span>Full Size</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* GPS Updated Bar & Simulation Toggle commented out (fake timer & simulation) */}
                {/*
                <div className="flex items-center justify-between pt-3 mt-auto text-xs">
                  <div className="flex items-center gap-1.5 text-emerald-700 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>GPS updated {lastUpdatedSec} sec ago</span>
                  </div>
                  <button
                    onClick={() => setGpsSimulatedLoss((prev) => !prev)}
                    className="border border-gray-200 rounded px-2.5 py-1 text-gray-600 hover:bg-gray-50 text-[11px] font-medium transition-colors cursor-pointer"
                  >
                    {gpsSimulatedLoss ? 'Restore GPS' : 'Preview GPS loss'}
                  </button>
                </div>
                */}
              </div>
            </div>
          </div>
        </div>

        {/* Modal Bottom Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between bg-white shrink-0">
          <div className="text-xs text-gray-500 font-medium">
            Booking #{bookingId} · Dispatch Overview
          </div>
          <div className="flex items-center gap-2">
            {!isEditingPatient && (
              <button
                type="button"
                onClick={startEditingPatient}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-blue-200 text-blue-700 bg-blue-50/70 hover:bg-blue-100/80 text-xs font-semibold transition-colors cursor-pointer shadow-2xs"
                title="Update Patient Information"
              >
                <Pencil className="w-3 h-3" />
                <span>Update Patient Info</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="border border-gray-300 rounded-lg px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer shadow-2xs"
            >
              Close overview
            </button>
          </div>
        </div>
      </div>

      {showLocationPopup && (
        <PatientLocationSharePopup
          target="pickup"
          phone={bookingPatientPhone(raw)}
          patientName={raw.patient_name}
          bookingId={trip.id}
          existingShareId={locationShare?.shareId}
          onClose={() => { setShowLocationPopup(false); refreshLocationShare(); }}
        />
      )}

      {showPaymentLink && (
        <PaymentLinkModal
          trip={trip}
          onClose={() => setShowPaymentLink(false)}
          onPaid={() => {
            setPaidViaLink(true);
            if (onPaymentLinkPaid) onPaymentLinkPaid(trip);
          }}
        />
      )}
    </div>
  );
};

export default CommandCenterTripDetailsModal;
